import moment from 'moment';
import { criteriaList, operatorLabel } from './criteriaTree';

export const MAX_PAST_SEARCHES = 5;

const STORAGE_PREFIX = 'dataExplorerPastSearches';

// Keyed by the signed-in user as well as the view: a search's criteria can name a patient, and a
// browser is often shared. With no user there is no history, read or written.
const storageKey = (ownerId, viewKey) =>
  `${STORAGE_PREFIX}:${ownerId}:${viewKey || '__none__'}`;

export function searchSignature(entry) {
  return JSON.stringify({
    viewKey: entry?.viewKey ?? null,
    columns: [...(entry?.columns || [])].sort(),
    criteria: entry?.criteria ?? null,
    dateFilter: entry?.dateFilter ?? null
  });
}

export function addPastSearch(list, entry, max = MAX_PAST_SEARCHES) {
  if (!entry) return Array.isArray(list) ? list : [];
  const signature = searchSignature(entry);
  const others = (Array.isArray(list) ? list : []).filter(
    (e) => searchSignature(e) !== signature
  );
  return [entry, ...others].slice(0, max);
}

export function removePastSearch(list, entry) {
  if (!entry) return Array.isArray(list) ? list : [];
  const signature = searchSignature(entry);
  return (Array.isArray(list) ? list : []).filter(
    (e) => searchSignature(e) !== signature
  );
}

export function buildPastSearch({
  view,
  columns,
  criteria,
  dateFilter,
  ranAt = new Date()
}) {
  return {
    ranAt: ranAt.toISOString(),
    viewKey: view?.key ?? null,
    viewLabel: view?.label ?? '',
    primaryModel: view?.primaryModel ?? null,
    columns: [...(columns || [])],
    criteria: criteria ?? null,
    dateFilter: dateFilter ? { ...dateFilter } : null
  };
}

export function loadPastSearches(viewKey, ownerId) {
  if (!ownerId) return [];
  try {
    const raw = window.localStorage.getItem(storageKey(ownerId, viewKey));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.slice(0, MAX_PAST_SEARCHES) : [];
  } catch {
    // Unreadable, absent, or storage blocked — an empty history is correct.
    return [];
  }
}

export function persistPastSearches(viewKey, list, ownerId) {
  if (!ownerId) return;
  try {
    window.localStorage.setItem(
      storageKey(ownerId, viewKey),
      JSON.stringify((list || []).slice(0, MAX_PAST_SEARCHES))
    );
  } catch {
    // Nothing to do and nothing worth telling the user — the history is a
    // convenience, and the search they just ran is on screen regardless.
  }
}

export function clearPastSearches(viewKey, ownerId) {
  if (!ownerId) return;
  try {
    window.localStorage.removeItem(storageKey(ownerId, viewKey));
  } catch {
    /* see persistPastSearches */
  }
}

// Every user's history, and any written before it was keyed by user. Called at sign-out, when the
// explorer's in-progress query is cleared too (explorerStatePersistence): a search naming a patient
// should not stay in a shared browser once its user has gone.
export function clearAllPastSearches() {
  try {
    const storage = window.localStorage;
    const keys = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && key.startsWith(`${STORAGE_PREFIX}:`)) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch {
    /* see persistPastSearches */
  }
}

function labelFor(schema, model, field) {
  const modelLabel = schema?.[model]?.label || model || '';
  const fieldLabel = schema?.[model]?.fields?.[field]?.label || field || '';
  return { modelLabel, fieldLabel };
}

/**
 * One criterion in the words the builder used to write it.
 *
 * Reads the operator from the shared vocabulary so the history cannot describe
 * a search differently from the box that built it.
 */
export function criterionSummary(criterion, schema) {
  if (!criterion) return '';
  const { modelLabel, fieldLabel } = labelFor(
    schema,
    criterion.model,
    criterion.field
  );
  const op = operatorLabel(criterion.op);
  const valuelessOp = criterion.op === 'exists' || criterion.op === 'notExists';
  const value = valuelessOp ? '' : String(criterion.value ?? '');
  return [modelLabel, fieldLabel, op, value].filter(Boolean).join(' ');
}

export function summarisePastSearch(entry, schema = {}, maxCriteria = 3) {
  const all = criteriaList(entry?.criteria).filter(Boolean);
  const shown = all.slice(0, maxCriteria);

  const dateFilter = entry?.dateFilter;
  const hasDateRange = !!(dateFilter?.dateFrom || dateFilter?.dateTo);
  let dateRange = '';
  if (hasDateRange) {
    const field = dateFilter.dateField || '';
    const dotIdx = field.indexOf('.');
    const { fieldLabel } =
      dotIdx > 0
        ? labelFor(schema, field.slice(0, dotIdx), field.slice(dotIdx + 1))
        : { fieldLabel: field };
    const from = dateFilter.dateFrom || '…';
    const to = dateFilter.dateTo || '…';
    dateRange = `${fieldLabel} ${from} → ${to}`.trim();
  }

  return {
    when: entry?.ranAt ? moment(entry.ranAt).fromNow() : '',
    viewLabel: entry?.viewLabel || '',
    columnCount: (entry?.columns || []).length,
    criteria: shown.map((c) => criterionSummary(c, schema)),
    extraCriteria: Math.max(0, all.length - shown.length),
    dateRange
  };
}
